// Pure builder for the Cost Report PDF as a pdfmake document definition.
// Counterpart to `variancePdf.js` — both produce REAL VECTOR TEXT (vs the
// raster image PDF html2pdf produces from BudgetPrintLayout). When both
// Cost Report and Variance Analysis are selected in the SharePdfButton
// extras, they are composed into a single pdfmake doc with a page-break
// between them (see BudgetOverview's renderPdfMakeDoc).
//
// No dependency on pdfmake itself: only declarative doc-def data is
// returned, so the layout stays pure and unit-testable.

const NAVY = '#1e3a5f';
const NAVY_LIGHT = '#2c4870';
const SECTION_BG = '#e8ecf0';
const ZEBRA = '#f8f8f8';
const SUBTOTAL_BG = '#f0f0f0';
const SECONDARY_FG = '#666666';
const GREY_RULE = '#dddddd';
const FOOTER_GREY = '#999999';

// Per-row spend health colours — matches BudgetPrintLayout's .budget-health-*
const HEALTH_GOOD = '#16a34a';
const HEALTH_WARN = '#d97706';
const HEALTH_DANGER = '#dc2626';
const NEGATIVE = '#dc2626';

const dateLong = () => new Date().toLocaleDateString('en-GB', {
  weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
});
const dateShort = () => new Date().toLocaleDateString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric',
});

const healthColor = (pct) => pct >= 90 ? HEALTH_DANGER : pct >= 70 ? HEALTH_WARN : HEALTH_GOOD;

/**
 * THE most older-version columns the Cost Report shows — on screen AND in this
 * PDF. ONE number, read by the Budget Overview's `Compare` picker
 * (`components/budget/BudgetComparePicker.js`) and by this builder, so what you
 * see is what you print. Owner, 2026-10-05: *"make sure that the x of x is
 * sensible so it fits on an A4 landscape print out"*.
 *
 * MEASURED 2026-10-05, not estimated: real PDFs built by THIS file through
 * pdfmake 0.2.20 (A4 landscape, 28pt margins), 8 cost codes named
 * "Pyrotechnic consumables and atmospheric effects hire", every figure a
 * 7-digit £ amount (£1,234,567.89; a negative -£1,234,567.89), versions named
 * "Revision 7 — Post-recce rebudget (Oct)"; word boxes read back with poppler
 * `pdftotext -bbox`.
 *   N=3 → 1 page, every £ figure whole, nothing past the right margin;
 *         Description ~2 lines per row.
 *   N=4 → money still whole, but Description falls to ~93pt (4-line rows) and
 *         the same report runs to 2 pages.
 *   N=5 → 20 figures run PAST THE PAGE EDGE (right edge 845.9pt on an
 *         841.9pt page) — the `*` Description column has nothing left to give.
 * So 3 — until 2026-10-06, when the owner asked for 4 ("I think theres room for
 * a 4th compare on A4?") knowing the cost above; the code column went 60→50 and
 * the two % columns 47→42 to give Description back 20pt. Re-measured by review
 * (same fixture, 8 rows): N=4 is now ONE page — the old widths still gave two —
 * with every header on one line and no £ figure split. N=5 still overflows.
 * 🪤 Separate, pre-existing, independent of N: an 8-digit figure
 * (£12,345,678.90) WRAPS in the 9pt bold GRAND TOTAL row — the numeric
 * columns are a fixed 65pt. Not changed here.
 */
export const MAX_COMPARE_VERSIONS = 4;

// ── Inner content (no page wrapper) ────────────────────────────────────
// Returns just the pdfmake `content[]` slice for the Cost Report. Useful
// when composing multi-section PDFs (e.g. Cost Report + Variance Analysis).
export function buildBudgetReportContent({
  reportData,
  departmentName,
  productionName,
  companyName,
  versionName,
  // Accepted for API compatibility with callers that also build the Variance
  // section, but NOT shown on the Cost Report header — this page is actual
  // spend vs the current budget, not a version-to-version comparison. The
  // baseline comparison lives on the Variance pages (budgetVariancePdf.js).
  baselineName, // eslint-disable-line no-unused-vars
  formatMoney,
  // Optional: the ids of the older versions to print (the Overview's Compare
  // picker). Absent → every older version, as before. Report order is kept;
  // never more than MAX_COMPARE_VERSIONS.
  compareVersionIds,
}) {
  if (!reportData) return [];
  const { cost_codes = [], crew_summary = {}, grand_total = {}, versions = [] } = reportData;
  const olderVersions = versions.filter(v => !v.is_current);
  const prevVersions = Array.isArray(compareVersionIds)
    ? olderVersions
      .filter(v => compareVersionIds.some(id => Number(id) === Number(v.id)))
      .slice(0, MAX_COMPARE_VERSIONS)
    : olderVersions;

  // Column widths — Description uses pdfmake's '*' star width to absorb
  // whatever space the page has left after the fixed numeric columns.
  // pdfmake handles the page-fitting maths internally (accounting for its
  // own cell padding + border overhead), so the table always fills the
  // page without ever overflowing the right margin, regardless of how
  // many prev-version columns are present.
  const widths = [
    50,                                          // code
    '*',                                         // desc — stretches to fill
    65,                                          // actual
    65,                                          // budget
    ...prevVersions.map(() => 65),               // each prev version
    65,                                          // unspent
    42,                                          // pctSpend
    42,                                          // pctLeft
  ];
  const colCount = widths.length;
  // Used for the navy header underline below — match the page width pdfmake
  // calculates internally.
  const HEADER_LINE_WIDTH = 786;

  // Header row
  const headerRow = [
    { text: 'Cost Code', fillColor: NAVY, color: 'white', bold: true, fontSize: 7.5 },
    { text: 'Description', fillColor: NAVY, color: 'white', bold: true, fontSize: 7.5 },
    { text: 'Cost to Date', fillColor: NAVY, color: 'white', bold: true, fontSize: 7.5, alignment: 'right' },
    {
      // Stacked label: small "Current Budget" caption above the bold
      // version name, so the reader can see at a glance which version
      // this column represents. Mirrors the Variance PDF's Current-column
      // treatment for visual consistency.
      stack: [
        { text: 'Current Budget', fontSize: 6, bold: false, color: '#bcd0ea', alignment: 'right' },
        { text: versionName || 'Current', fontSize: 8, bold: true, color: 'white', alignment: 'right' },
      ],
      fillColor: NAVY,
    },
    ...prevVersions.map(v => ({
      text: v.version_name, fillColor: NAVY, color: 'white', bold: true, fontSize: 7.5, alignment: 'right',
    })),
    { text: 'Unspent', fillColor: NAVY, color: 'white', bold: true, fontSize: 7.5, alignment: 'right' },
    { text: '% Spend', fillColor: NAVY, color: 'white', bold: true, fontSize: 7.5, alignment: 'right' },
    { text: '% Left', fillColor: NAVY, color: 'white', bold: true, fontSize: 7.5, alignment: 'right' },
  ];

  // Row factories
  const sectionRow = (label) => [{
    text: label,
    colSpan: colCount,
    fillColor: SECTION_BG,
    color: '#444',
    bold: true,
    fontSize: 7.5,
    characterSpacing: 0.5,
    margin: [2, 2, 2, 2],
  }, ...Array(colCount - 1).fill({})];

  // A full-width section HEADING, styled to match the navy "Cost Code" column
  // header (navy fill, white, bold). Used for true headings like "Labour" —
  // distinct from sectionRow's lighter subtotal-style band.
  const headingRow = (label) => [{
    text: label,
    colSpan: colCount,
    fillColor: NAVY,
    color: 'white',
    bold: true,
    fontSize: 7.5,
    characterSpacing: 0.5,
    margin: [2, 2, 2, 2],
  }, ...Array(colCount - 1).fill({})];

  const subtotalRow = (label, perColCell) => {
    // perColCell(colIdx) returns the cell content for each data column.
    // colIdx ranges over the actual data columns (everything after the label
    // colSpan), so 0 = actual, 1 = budget, then prev versions, then unspent,
    // then % spend, then % left.
    const cells = [{
      text: label, colSpan: 2, fillColor: SUBTOTAL_BG, bold: true, fontSize: 8,
    }, {}];
    const dataCols = 2 + prevVersions.length + 3; // actual + budget + N prev + unspent + %Spend + %Left
    for (let i = 0; i < dataCols; i++) {
      cells.push({
        ...perColCell(i),
        fillColor: SUBTOTAL_BG,
        bold: true,
        fontSize: 8,
        alignment: 'right',
      });
    }
    return cells;
  };

  const grandRow = (label, perColCell) => {
    const cells = [{
      text: label, colSpan: 2, fillColor: NAVY, color: 'white', bold: true, fontSize: 9,
      margin: [2, 3, 2, 3],
    }, {}];
    const dataCols = 2 + prevVersions.length + 3;
    for (let i = 0; i < dataCols; i++) {
      cells.push({
        ...perColCell(i),
        fillColor: NAVY,
        color: 'white',
        bold: true,
        fontSize: 9,
        alignment: 'right',
        margin: [2, 3, 2, 3],
      });
    }
    return cells;
  };

  const body = [headerRow];

  // ── Purchases & Rentals ─────────────────────────────────────────────
  body.push(sectionRow('Purchases & Rentals'));
  cost_codes.forEach(cc => {
    const budget = parseFloat(cc.budget_amount) || 0;
    const spent = parseFloat(cc.cost_to_date) || 0;
    const unspent = budget - spent;
    const pctSpend = budget > 0 ? (spent / budget) * 100 : (spent > 0 ? 100 : 0);
    const pctLeft = budget > 0 ? Math.max(0, 100 - pctSpend) : 0;
    body.push([
      { text: cc.code, fontSize: 7.5 },
      { text: cc.name, fontSize: 8 },
      { text: formatMoney(spent), alignment: 'right', fontSize: 8 },
      { text: formatMoney(budget), alignment: 'right', fontSize: 8 },
      ...prevVersions.map(v => {
        const val = cc.version_budgets?.[v.id];
        return {
          text: val != null ? formatMoney(val) : '—',
          alignment: 'right', fontSize: 7, color: SECONDARY_FG,
        };
      }),
      {
        text: formatMoney(unspent),
        alignment: 'right', fontSize: 8,
        color: unspent < 0 ? NEGATIVE : undefined,
        bold: unspent < 0,
      },
      {
        text: `${pctSpend.toFixed(1)}%`,
        alignment: 'right', fontSize: 8,
        color: healthColor(pctSpend),
        bold: pctSpend >= 90,
      },
      { text: `${pctLeft.toFixed(1)}%`, alignment: 'right', fontSize: 8 },
    ]);
  });

  // Purchases subtotal
  body.push(subtotalRow('Total Purchases & Rentals', (i) => {
    if (i === 0) return { text: formatMoney(grand_total.purchases_actual || 0) };
    if (i === 1) return { text: formatMoney(grand_total.purchases_budget || 0) };
    // prev versions: each shows its total_budget
    if (i >= 2 && i < 2 + prevVersions.length) {
      const v = prevVersions[i - 2];
      return { text: formatMoney(v.total_budget || 0), fontSize: 7 };
    }
    const offsetFromEnd = i - (2 + prevVersions.length);
    if (offsetFromEnd === 0) {
      return { text: formatMoney((grand_total.purchases_budget || 0) - (grand_total.purchases_actual || 0)) };
    }
    if (offsetFromEnd === 1) {
      const pct = grand_total.purchases_budget > 0
        ? ((grand_total.purchases_actual / grand_total.purchases_budget) * 100).toFixed(1) + '%'
        : '—';
      return { text: pct };
    }
    if (offsetFromEnd === 2) {
      const pct = grand_total.purchases_budget > 0
        ? Math.max(0, 100 - (grand_total.purchases_actual / grand_total.purchases_budget) * 100).toFixed(1) + '%'
        : '—';
      return { text: pct };
    }
    return { text: '' };
  }));

  // ── Labour section ──────────────────────────────────────────────────
  body.push(headingRow('Labour'));

  // Labour has live actuals too (accrued weeks-to-date × rate), which the
  // server returns and folds into grand_total. Mirror the on-screen Cost
  // Report (BudgetOverview.js ~920-1130): show Cost-to-Date, Unspent and the
  // % columns for each labour row so they reconcile with the GRAND TOTAL.
  const pctSpend = (actual, budget) => (budget > 0 ? `${((actual / budget) * 100).toFixed(1)}%` : '—');
  const pctLeft = (actual, budget) => (budget > 0 ? `${Math.max(0, 100 - (actual / budget) * 100).toFixed(1)}%` : '—');

  // Crew Rates
  const totLab = crew_summary.total_labour || 0;
  const totLabActual = crew_summary.total_labour_actual || 0;
  body.push([
    { text: 'Crew Rates', colSpan: 2, fontSize: 8 }, {},
    { text: formatMoney(totLabActual), alignment: 'right', fontSize: 8 },
    { text: formatMoney(totLab), alignment: 'right', fontSize: 8 },
    ...prevVersions.map(v => ({
      text: v.crew_labour != null ? formatMoney(v.crew_labour) : '—',
      alignment: 'right', fontSize: 7, color: SECONDARY_FG,
    })),
    { text: formatMoney(totLab - totLabActual), alignment: 'right', fontSize: 8 },
    { text: pctSpend(totLabActual, totLab), alignment: 'right', fontSize: 8 },
    { text: pctLeft(totLabActual, totLab), alignment: 'right', fontSize: 8 },
  ]);

  // Total Allowances — Computer rental + named allowance slots (Box, Phone,
  // Travel…). Matches the on-screen Cost Report's "Total Allowances" row at
  // BudgetOverview.js:1014. Falls back to total_computer_rental(_actual) for
  // older payloads that predate the total_allowances field.
  const totalAllow = crew_summary.total_allowances ?? crew_summary.total_computer_rental ?? 0;
  const totalAllowActual = crew_summary.total_allowances_actual ?? crew_summary.total_computer_rental_actual ?? 0;
  body.push([
    { text: 'Total Allowances', colSpan: 2, fontSize: 8 }, {},
    { text: formatMoney(totalAllowActual), alignment: 'right', fontSize: 8 },
    { text: formatMoney(totalAllow), alignment: 'right', fontSize: 8 },
    ...prevVersions.map(v => {
      // Each prev version's full allowance sum = computer subtotal +
      // named-allowance subtotal. Both come from computeVersionAggregates.
      const hasAny = v.crew_computer != null || v.crew_allowance != null;
      const val = (Number(v.crew_computer) || 0) + (Number(v.crew_allowance) || 0);
      return {
        text: hasAny ? formatMoney(val) : '—',
        alignment: 'right', fontSize: 7, color: SECONDARY_FG,
      };
    }),
    { text: formatMoney(totalAllow - totalAllowActual), alignment: 'right', fontSize: 8 },
    { text: pctSpend(totalAllowActual, totalAllow), alignment: 'right', fontSize: 8 },
    { text: pctLeft(totalAllowActual, totalAllow), alignment: 'right', fontSize: 8 },
  ]);

  // Per-allowance breakdown child rows — Computer + named slots (Box, Phone,
  // Travel…), indented under Total Allowances. Mirrors the on-screen expand at
  // BudgetOverview.js:1062. Data is on crew_summary.allowance_breakdown (active
  // version) + each prev version's crew_allowance_by_name (case-folded name →
  // value). Missing name in a prev version → "—" (allowance didn't exist then).
  const allowanceBreakdown = crew_summary.allowance_breakdown || [];
  allowanceBreakdown.forEach(item => {
    const childBudget = item.budgeted || 0;
    const childActual = item.actual || 0;
    body.push([
      { text: item.name, colSpan: 2, fontSize: 8, italics: true, color: SECONDARY_FG, margin: [10, 0, 0, 0] }, {},
      { text: formatMoney(childActual), alignment: 'right', fontSize: 8, color: SECONDARY_FG },
      { text: formatMoney(childBudget), alignment: 'right', fontSize: 8, color: SECONDARY_FG },
      ...prevVersions.map(v => {
        const key = String(item.name || '').toLowerCase();
        const val = v.crew_allowance_by_name ? v.crew_allowance_by_name[key] : undefined;
        return {
          text: val != null ? formatMoney(val) : '—',
          alignment: 'right', fontSize: 7, color: SECONDARY_FG,
        };
      }),
      { text: formatMoney(childBudget - childActual), alignment: 'right', fontSize: 8, color: SECONDARY_FG },
      { text: pctSpend(childActual, childBudget), alignment: 'right', fontSize: 8, color: SECONDARY_FG },
      { text: pctLeft(childActual, childBudget), alignment: 'right', fontSize: 8, color: SECONDARY_FG },
    ]);
  });

  // 🔀 JOB COST CODES (2026-10-28): the ad-hoc lines that printed here are
  // job codes now — they print with the cost codes, and labour is crew only.

  // Labour subtotal
  const labBudget = grand_total.labour_budget || 0;
  const labActual = grand_total.labour_actual || 0;
  body.push(subtotalRow('Total Labour', (i) => {
    if (i === 0) return { text: formatMoney(labActual) };
    if (i === 1) return { text: formatMoney(labBudget) };
    if (i >= 2 && i < 2 + prevVersions.length) {
      const v = prevVersions[i - 2];
      return { text: v.crew_total != null ? formatMoney(v.crew_total) : '—', fontSize: 7 };
    }
    const offsetFromEnd = i - (2 + prevVersions.length);
    if (offsetFromEnd === 0) return { text: formatMoney(labBudget - labActual) };
    if (offsetFromEnd === 1) return { text: pctSpend(labActual, labBudget) };
    if (offsetFromEnd === 2) return { text: pctLeft(labActual, labBudget) };
    return { text: '' };
  }));

  // Grand total
  body.push(grandRow('GRAND TOTAL', (i) => {
    if (i === 0) return { text: formatMoney(grand_total.total_actual || 0) };
    if (i === 1) return { text: formatMoney(grand_total.total_budget || 0) };
    if (i >= 2 && i < 2 + prevVersions.length) {
      const v = prevVersions[i - 2];
      const total = (v.total_budget || 0) + (v.crew_total || 0);
      return { text: formatMoney(total), fontSize: 7.5 };
    }
    const offsetFromEnd = i - (2 + prevVersions.length);
    if (offsetFromEnd === 0) {
      return { text: formatMoney((grand_total.total_budget || 0) - (grand_total.total_actual || 0)) };
    }
    if (offsetFromEnd === 1) {
      const pct = grand_total.total_budget > 0
        ? ((grand_total.total_actual / grand_total.total_budget) * 100).toFixed(1) + '%'
        : '—';
      return { text: pct };
    }
    if (offsetFromEnd === 2) {
      const pct = grand_total.total_budget > 0
        ? Math.max(0, 100 - (grand_total.total_actual / grand_total.total_budget) * 100).toFixed(1) + '%'
        : '—';
      return { text: pct };
    }
    return { text: '' };
  }));

  // Assemble content
  const content = [
    {
      // Header band: title on the left, dept/date right-aligned on the
      // SAME row. Saves vertical space vs stacking them. The right column
      // gets a small top margin so the dept-line baseline visually aligns
      // with the 18pt title baseline.
      columns: [
        {
          width: '*',
          text: (() => {
            const subtitle = productionName || companyName;
            // Lead with the department so the report self-identifies, e.g.
            // "SFX Cost Report — PROJECT AURORA".
            const titleText = `${departmentName ? `${departmentName} ` : ''}Cost Report`;
            if (!subtitle) return titleText;
            return [
              { text: titleText, fontSize: 18, bold: true },
              { text: `  —  ${subtitle.toUpperCase()}`, fontSize: 13, bold: true },
            ];
          })(),
          color: NAVY,
        },
        {
          // Just the date — the department now lives in the title on the left,
          // so repeating it here would duplicate it on the same row.
          width: 'auto',
          text: dateLong().toUpperCase(),
          fontSize: 10, color: '#555', alignment: 'right',
          margin: [0, 9, 0, 0],
        },
      ],
      margin: [0, 0, 0, 2],
    },
    // Standalone company line only when both production AND company exist
    // (otherwise the inlined subtitle in the title already covers it).
    productionName && companyName && {
      text: companyName.toUpperCase(),
      fontSize: 11, bold: true, color: '#333', margin: [0, 0, 0, 1],
    },
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: HEADER_LINE_WIDTH, y2: 0, lineWidth: 2, lineColor: NAVY }],
      margin: [0, 0, 0, 4],
    },
    versionName && {
      // Describe what THIS page shows: actual spend (Cost to Date) measured
      // against the current budget version — mirrors the table's "Cost to
      // Date" + "Current Budget" column labels. The "vs baseline (…)"
      // version comparison is a Variance Analysis concept and lives on the
      // Variance pages (budgetVariancePdf.js), not here. Bold navy 11pt so
      // the reader can't miss which budget this report covers.
      text: `Cost to date vs current budget (${versionName})`,
      bold: true, color: '#1e3a5f', fontSize: 11, margin: [0, 0, 0, 8],
    },
    {
      table: { headerRows: 1, widths, body, dontBreakRows: true },
      layout: {
        fillColor: (rowIndex) => (rowIndex > 0 && rowIndex % 2 === 0 ? ZEBRA : null),
        hLineColor: () => GREY_RULE,
        vLineColor: () => GREY_RULE,
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        paddingTop: () => 2,
        paddingBottom: () => 2,
        paddingLeft: () => 4,
        paddingRight: () => 4,
      },
    },
  ].filter(Boolean);

  return content;
}

// Full doc wrapper for callers that just want a self-contained Cost Report PDF.
export function buildBudgetReportDoc(args) {
  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [28, 32, 28, 36],
    info: {
      title: `${args.departmentName} Cost Report`,
      author: args.companyName || 'Crew Forge',
      subject: 'Department Cost Report',
    },
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `Printed on ${dateShort()} | ${args.departmentName || ''} Cost Report | Crew Forge`,
          fontSize: 7, color: FOOTER_GREY, alignment: 'left',
          margin: [28, 8, 0, 0],
        },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          fontSize: 7, color: FOOTER_GREY, alignment: 'right',
          margin: [0, 8, 28, 0],
        },
      ],
    }),
    defaultStyle: { fontSize: 9, color: '#000' },
    content: buildBudgetReportContent(args),
  };
}
